/**
 * LmsCatalogWorkspace — Phase 10.6 LMS catalog management.
 *
 * Lives under `/management?tab=lms`. All underlying API endpoints live in
 * `api/src/routes/management.ts`.
 *
 * Three explicit-trigger cards (LMS responses are expensive HMAC-signed
 * calls — no auto-fetch):
 *
 *   01 Porovnání modelů — POST /v1/management/lms/compare-models →
 *      side-by-side LMS vs. DB model list, surface ID mismatches.
 *   02 Skladové vozy z LMS — GET /v1/management/lms/stock-vehicles →
 *      raw inventory rows from the factory.
 *   03 Porovnání skladu — POST /v1/management/lms/compare-stock →
 *      4 grouped sections (outdated · up-to-date with differences ·
 *      unmatched in LMS · factory-only but in DB). Each diff row
 *      exposes per-field "← LMS" buttons that fire PATCH
 *      /v1/management/vehicles/{id} to sync the field from LMS.
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Marker } from '@/components/Marker';
import { Skeleton } from '@/components/Skeleton';
import { Toolband } from '@/components/Toolband';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { formatDate, formatInt } from '@/lib/format';
import {
  useLmsCompareModelsMutation,
  useLmsCompareStockMutation,
  useLmsPrevzitMutation,
  useLmsStockVehiclesMutation,
  useVehiclePatchMutation,
  type LmsComparisonItem,
  type LmsDbCar,
  type LmsExternalCar,
  type LmsModel,
} from '@/modules/management/api';

interface LmsCatalogWorkspaceProps {
  isAdmin: boolean;
}

export function LmsCatalogWorkspace({ isAdmin }: LmsCatalogWorkspaceProps) {
  if (!isAdmin) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5">
          <EmptyState
            eyebrow="403"
            title="LMS katalog je dostupný jen administrátorům."
            description="Pro přístup kontaktuj správce firmy."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Toolband>
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          LMS katalog
        </span>
        <Toolband.Spacer />
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Volání podepisuje API serverovým HMAC klíčem firmy.
        </span>
      </Toolband>

      <div
        data-testid="management-lms"
        className="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-8 pb-8 pt-5 max-sm:px-4"
      >
        <CompareModelsCard />
        <StockVehiclesCard />
        <CompareStockCard />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   01 Porovnání modelů
   ───────────────────────────────────────────────────────────────────────── */

function CompareModelsCard() {
  const run = useLmsCompareModelsMutation();

  return (
    <Card
      n="01"
      title="Porovnání modelů"
      description="Stáhne aktuální seznam modelů z LMS a porovná je s lokální tabulkou modelů — odhalí chybějící mapování LMS ID."
      action={
        <Button
          variant="primary"
          size="sm"
          icon={<Icon name="download" size={12} />}
          onClick={() => run.mutate(undefined, { onError: (e) => toast.error(lmsErr(e)) })}
          disabled={run.isPending}
        >
          {run.isPending ? 'Načítám…' : 'Načíst porovnání'}
        </Button>
      }
    >
      {run.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : run.data ? (
        <ModelsDiffTable lmsModels={run.data.lms_models} dbModels={run.data.db_models} />
      ) : (
        <CardEmpty>Klikněte „Načíst porovnání“ pro stažení dat z LMS.</CardEmpty>
      )}
    </Card>
  );
}

interface ModelsDiffTableProps {
  lmsModels: LmsModel[];
  dbModels: { id: number; lms_id: number; nazev_modelu: string }[];
}

function lmsModelId(m: LmsModel): number {
  // LMS API returns the field as `ID` (uppercase). Tolerate other casings
  // just in case, but `ID` is the canonical one per dmsapi.fordissimo.cz.
  const raw = m.ID ?? m.LMS_ID ?? m.Id ?? m.id ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function ModelsDiffTable({ lmsModels, dbModels }: ModelsDiffTableProps) {
  // Map DB models by their LMS mapping; mapped rows render alongside their
  // LMS counterpart, unmapped ones drop to the bottom so admins can spot
  // models that still need an `lms_id` assigned.
  const dbByLmsId = new Map<number, { id: number; lms_id: number; nazev_modelu: string }>();
  const unmappedDb: { id: number; lms_id: number; nazev_modelu: string }[] = [];
  for (const m of dbModels) {
    if (m.lms_id > 0) {
      dbByLmsId.set(m.lms_id, m);
    } else {
      unmappedDb.push(m);
    }
  }

  const lmsIds = new Set<number>();
  for (const m of lmsModels) {
    const id = lmsModelId(m);
    if (id > 0) lmsIds.add(id);
  }
  for (const id of dbByLmsId.keys()) lmsIds.add(id);
  // Sort descending — newest models first, as in the legacy page.
  const sortedIds = [...lmsIds].sort((a, b) => b - a);

  if (sortedIds.length === 0 && unmappedDb.length === 0) {
    return <CardEmpty>Žádné modely neodpovídají.</CardEmpty>;
  }

  const gridCols = 'grid-cols-[70px_80px_minmax(0,1fr)_minmax(0,1fr)_120px]';

  return (
    // Phone (14.8): the 5-column diff grid is column-dependent — it scrolls
    // horizontally below `sm` instead of collapsing (header/row alignment).
    <div className="overflow-hidden border border-line bg-paper max-sm:overflow-x-auto">
      <div
        className={`grid ${gridCols} border-b border-line bg-bg px-3 py-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3 max-sm:min-w-[560px]`}
      >
        <span>LMS ID</span>
        <span>LMS DB ID</span>
        <span>LMS název</span>
        <span>DB název</span>
        <span className="text-right">Shoda</span>
      </div>
      <ul className="divide-y divide-line max-sm:min-w-[560px]">
        {sortedIds.map((id) => {
          const lmsRow = lmsModels.find((m) => lmsModelId(m) === id);
          const dbRow = dbByLmsId.get(id);
          const matched = !!lmsRow && !!dbRow;
          return (
            <li
              key={`lms-${id}`}
              className={`grid ${gridCols} items-center gap-2 px-3 py-2 text-[12.5px]`}
            >
              <span className="font-mono tabular-nums text-ink-3">{id}</span>
              <span className="font-mono tabular-nums text-ink-3">{dbRow?.lms_id ?? '—'}</span>
              <span className="truncate text-ink">
                {String(lmsRow?.Nazev ?? lmsRow?.Model ?? '—')}
              </span>
              <span className="truncate text-ink">{dbRow?.nazev_modelu ?? '—'}</span>
              <span className="text-right">
                {matched ? (
                  <Marker tone="ok">Spárováno</Marker>
                ) : lmsRow ? (
                  <Marker tone="warn">Chybí v DB</Marker>
                ) : (
                  <Marker tone="neutral">Chybí v LMS</Marker>
                )}
              </span>
            </li>
          );
        })}
        {unmappedDb.map((m) => (
          <li
            key={`db-${m.id}`}
            className={`grid ${gridCols} items-center gap-2 px-3 py-2 text-[12.5px]`}
          >
            <span className="font-mono tabular-nums text-ink-3">—</span>
            <span className="font-mono tabular-nums text-ink-3">—</span>
            <span className="truncate text-ink">—</span>
            <span className="truncate text-ink">{m.nazev_modelu}</span>
            <span className="text-right">
              <Marker tone="warn">Bez mapování</Marker>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   02 Skladové vozy z LMS
   ───────────────────────────────────────────────────────────────────────── */

function StockVehiclesCard() {
  const run = useLmsStockVehiclesMutation();
  const rows = run.data?.stock_vehicles ?? [];

  return (
    <Card
      n="02"
      title="Skladové vozy z LMS"
      description="Stáhne aktuální stav skladu z LMS — VIN, interní číslo, status a další klíčová pole."
      action={
        <Button
          variant="primary"
          size="sm"
          icon={<Icon name="download" size={12} />}
          onClick={() => run.mutate(undefined, { onError: (e) => toast.error(lmsErr(e)) })}
          disabled={run.isPending}
        >
          {run.isPending ? 'Načítám…' : 'Načíst sklad'}
        </Button>
      }
    >
      {run.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : run.data ? (
        rows.length === 0 ? (
          <CardEmpty>LMS aktuálně neevidují žádné skladové vozy.</CardEmpty>
        ) : (
          <StockTable rows={rows} />
        )
      ) : (
        <CardEmpty>Klikněte „Načíst sklad“ pro stažení aktuálního stavu z LMS.</CardEmpty>
      )}
    </Card>
  );
}

interface StockTableProps {
  rows: LmsExternalCar[];
}

function StockTable({ rows }: StockTableProps) {
  return (
    // Phone (14.8): fixed-width 5-column stock grid scrolls horizontally
    // below `sm` (see ModelsDiffTable).
    <div className="overflow-hidden border border-line bg-paper max-sm:overflow-x-auto">
      <div className="grid grid-cols-[110px_120px_minmax(0,1fr)_140px_120px] border-b border-line bg-bg px-3 py-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3 max-sm:min-w-[640px]">
        <span>VIN</span>
        <span>Interní č.</span>
        <span>Model</span>
        <span>Status</span>
        <span>Smlouva</span>
      </div>
      <ul className="divide-y divide-line max-sm:min-w-[640px]">
        {rows.map((r, idx) => {
          const vin = String(r.VIN ?? '—');
          const vc = String(r.InterniCisloVozu ?? '—');
          const model = String(r.Model ?? r.Provedeni ?? '—');
          const status = String(r.SEERStatus ?? r.StatusCombined ?? '—');
          const smlouva =
            r.Smlouva === true || r.Smlouva === 1 || r.Smlouva === '1' ? 'Zákaznický' : 'Skladový';
          return (
            <li
              key={`${vin}-${vc}-${idx}`}
              className="grid grid-cols-[110px_120px_minmax(0,1fr)_140px_120px] items-center gap-2 px-3 py-1.5 text-[12px]"
            >
              <span className="truncate font-mono tabular-nums text-ink-2">{vin}</span>
              <span className="truncate font-mono tabular-nums text-ink-2">{vc}</span>
              <span className="truncate text-ink">{model}</span>
              <span className="truncate text-ink-2">{status}</span>
              <span className="text-ink-2">{smlouva}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   03 Porovnání skladu
   ───────────────────────────────────────────────────────────────────────── */

function CompareStockCard() {
  const run = useLmsCompareStockMutation();
  const data = run.data;

  return (
    <Card
      n="03"
      title="Porovnání skladu"
      description="Detailní porovnání lokální databáze proti LMS skladu. Vyznačí zastaralé záznamy, rozdíly v polích a vozy, které v jedné ze stran chybí."
      action={
        <Button
          variant="primary"
          size="sm"
          icon={<Icon name="download" size={12} />}
          onClick={() => run.mutate(undefined, { onError: (e) => toast.error(lmsErr(e)) })}
          disabled={run.isPending}
        >
          {run.isPending ? 'Načítám…' : 'Načíst porovnání'}
        </Button>
      }
    >
      {run.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : data ? (
        <div className="flex flex-col gap-4">
          <ComparisonSection
            title="Zastaralé"
            tone="warn"
            description="Lokální databáze má starší datum poslední změny než LMS — pravděpodobně potřebují aktualizaci."
            items={data.outdated}
            highlight
          />
          <ComparisonSection
            title="Aktuální s rozdíly"
            tone="accent"
            description="Datum změny je aktuální, ale jednotlivá pole se liší. Můžete je inline doplnit z LMS."
            items={data.upToDateWithDifferences}
            highlight
          />
          <ExternalOnlySection
            title="Pouze v LMS"
            description="Vozy ze skladu LMS, které lokální databáze neeviduje. „Převzít“ vůz založí i s výbavou z katalogu LMS."
            rows={data.unmatchedExternal}
          />
          <FactoryOnlySection
            title="V LMS jen jako tovární, ale v DB existují"
            description="Záznamy shodné podle VIN, kde lokální stav neodpovídá fáze „skladový/zákaznický“."
            rows={data.factoryOnlyButInDb}
          />
          <EqualSection
            title="Shodné"
            count={data.completelyEqual.length}
          />
        </div>
      ) : (
        <CardEmpty>Klikněte „Načíst porovnání“ pro plnou diff analýzu skladu.</CardEmpty>
      )}
    </Card>
  );
}

interface ComparisonSectionProps {
  title: string;
  tone: 'warn' | 'accent';
  description: string;
  items: LmsComparisonItem[];
  highlight?: boolean;
}

function ComparisonSection({ title, tone, description, items, highlight }: ComparisonSectionProps) {
  if (items.length === 0) {
    return (
      <section className="border border-line bg-paper">
        <SectionHeader tone={tone} title={title} count={0} />
        <p className="px-4 py-3 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Žádné záznamy.
        </p>
      </section>
    );
  }
  return (
    <section className="border border-line bg-paper">
      <SectionHeader tone={tone} title={title} count={items.length} description={description} />
      <ul className="divide-y divide-line">
        {items.map((item, idx) => (
          <li key={`${item.db_car.id_vozidla}-${idx}`} className="px-4 py-3">
            <ComparisonRow item={item} allowPatch={!!highlight} />
          </li>
        ))}
      </ul>
    </section>
  );
}

interface ComparisonRowProps {
  item: LmsComparisonItem;
  allowPatch: boolean;
}

/**
 * Field set mirrors the backend `fieldMappings` in
 * [api/src/services/lms.service.ts](../../../../api/src/services/lms.service.ts):199-209.
 * The backend only emits entries in `differences` for fields that actually
 * differ; we render every mapped field here so users can see *what matches*
 * for context (the legacy page worked the same way).
 */
const COMPARISON_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'barva', label: 'Barva' },
  { key: 'motor', label: 'Motor' },
  { key: 'prevodovka', label: 'Převodovka' },
  { key: 'calouneni', label: 'Čalounění' },
  { key: 'cenikova_cena', label: 'Ceníková cena' },
  { key: 'poznamka', label: 'Poznámka' },
  { key: 'vin', label: 'VIN' },
  { key: 'provedeni', label: 'Provedení' },
  { key: 'umisteni', label: 'Status/Umístění' },
];

function ComparisonRow({ item, allowPatch }: ComparisonRowProps) {
  const patch = useVehiclePatchMutation();
  const [patched, setPatched] = useState<Record<string, boolean>>({});
  const diffCount = Object.keys(item.differences).length;
  // Diffs not in COMPARISON_FIELDS (stav_vozidla, vybava) — append at the end.
  const baseKeys = new Set(COMPARISON_FIELDS.map((f) => f.key));
  const extraDiffs = Object.entries(item.differences).filter(
    ([k]) => !baseKeys.has(k),
  );

  const onSyncField = (field: string, value: string) => {
    patch.mutate(
      { id: item.db_car.id_vozidla, body: { field, value } },
      {
        onSuccess: () => {
          setPatched((p) => ({ ...p, [field]: true }));
          toast.ok(`${field} synchronizováno`);
        },
        onError: (err) => toast.error(lmsErr(err)),
      },
    );
  };

  const carNum = item.db_car.vc_auta || item.external_car.InterniCisloVozu || '—';
  const fakturaInfo = formatFakturaInfo(item.db_car);
  const dbDate = String(item.db_car.datum_posledni_zmeny ?? '').trim();
  const extDate = String(item.external_car.DatumPosledniZmeny ?? '').trim();

  return (
    <div className="flex flex-col gap-2">
      <header className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[12.5px]">
          <span className="font-mono tabular-nums text-ink-3">
            #{item.db_car.id_vozidla}
          </span>
          {item.db_car.id_vozidla ? (
            <Link
              to="/vozidla/$id"
              params={{ id: String(item.db_car.id_vozidla) }}
              className="font-mono text-accent-text underline decoration-dotted underline-offset-2 hover:decoration-solid"
            >
              {carNum}
            </Link>
          ) : (
            <span className="font-mono text-ink">{carNum}</span>
          )}
          {item.db_car.stav_vozidla ? (
            <Marker tone={stavTone(item.db_car.stav_vozidla)}>
              {item.db_car.stav_vozidla}
            </Marker>
          ) : null}
          <span className="ml-auto font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
            {diffCount} pole{diffCount === 1 ? '' : '·'} odlišných
          </span>
        </div>
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-[12px] text-ink-2 sm:grid-cols-[auto_minmax(0,1fr)_auto_minmax(0,1fr)]">
          <dt className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">VIN</dt>
          <dd className="truncate font-mono tabular-nums">
            {item.db_car.vin || item.external_car.VIN || '—'}
          </dd>
          <dt className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
            Vůz
          </dt>
          <dd className="truncate">
            {[item.db_car.znacka_nazev, item.db_car.model_nazev, item.db_car.provedeni]
              .filter(Boolean)
              .join(' · ') || '—'}
          </dd>
          <dt className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
            Faktura
          </dt>
          <dd className="truncate">{fakturaInfo}</dd>
          <dt className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
            Změna
          </dt>
          <dd className="truncate">
            <span>DB {formatChangeDate(dbDate) || '—'}</span>
            <span className="mx-2 text-ink-3">·</span>
            <span>LMS {formatChangeDate(extDate) || '—'}</span>
          </dd>
        </dl>
      </header>
      {/* Phone (14.8): the 4-column field-diff grid scrolls horizontally
          below `sm` — collapsing it would break header/row alignment. The
          wrapper is style-free at `sm+`, so desktop/tablet are unchanged. */}
      <div className="flex flex-col gap-2 max-sm:overflow-x-auto">
        <div className="grid grid-cols-[120px_minmax(0,1fr)_minmax(0,1fr)_110px] items-center gap-2 border border-line bg-bg px-2 py-1.5 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3 max-sm:min-w-[480px]">
          <span>Pole</span>
          <span>Databáze</span>
          <span>LMS</span>
          <span className="text-right">Akce</span>
        </div>
        <ul className="divide-y divide-line border border-t-0 border-line bg-paper max-sm:min-w-[480px]">
          {COMPARISON_FIELDS.map(({ key, label }) => {
            const diff = item.differences[key];
            const synced = patched[key];
            if (diff) {
              return (
                <DiffFieldRow
                  key={key}
                  field={key}
                  label={diff.field_label || label}
                  database={diff.database}
                  external={diff.external}
                  allowPatch={allowPatch}
                  synced={synced}
                  disabled={patch.isPending}
                  onSync={() => onSyncField(key, diff.external)}
                />
              );
            }
            const value = String(item.db_car[key] ?? '').trim();
            return <MatchFieldRow key={key} label={label} value={value} />;
          })}
          {extraDiffs.map(([key, diff]) => {
            const synced = patched[key];
            return (
              <DiffFieldRow
                key={key}
                field={key}
                label={diff.field_label}
                database={diff.database}
                external={diff.external}
                allowPatch={allowPatch}
                synced={synced}
                disabled={patch.isPending}
                onSync={() => onSyncField(key, diff.external)}
              />
            );
          })}
        </ul>
      </div>
    </div>
  );
}

interface DiffFieldRowProps {
  field: string;
  label: string;
  database: string;
  external: string;
  allowPatch: boolean;
  synced: boolean | undefined;
  disabled: boolean;
  onSync: () => void;
}

function DiffFieldRow({
  field,
  label,
  database,
  external,
  allowPatch,
  synced,
  disabled,
  onSync,
}: DiffFieldRowProps) {
  return (
    <li
      className={
        'grid grid-cols-[120px_minmax(0,1fr)_minmax(0,1fr)_110px] items-start gap-2 border-l-2 border-l-warn px-2 py-1.5 text-[12px] ' +
        (synced ? 'bg-[var(--accent-soft)]' : 'bg-[var(--warn-soft)]')
      }
    >
      <span className="font-mono uppercase tracking-[0.04em] text-ink-2">{label}</span>
      <span className="whitespace-pre-wrap text-ink-2">{database || '—'}</span>
      <span className="whitespace-pre-wrap text-ink">{external || '—'}</span>
      <span className="text-right">
        {allowPatch && !synced && isPatchableField(field) ? (
          <button
            type="button"
            onClick={onSync}
            disabled={disabled}
            className="border border-line bg-paper px-2 py-1 font-mono text-[10.5px] uppercase tracking-[0.08em] text-accent-text hover:border-accent disabled:opacity-50"
          >
            ← LMS
          </button>
        ) : synced ? (
          <Marker tone="ok">Hotovo</Marker>
        ) : (
          <span className="font-mono text-[10px] text-ink-3">—</span>
        )}
      </span>
    </li>
  );
}

function MatchFieldRow({ label, value }: { label: string; value: string }) {
  const display = value || '—';
  return (
    <li className="grid grid-cols-[120px_minmax(0,1fr)_minmax(0,1fr)_110px] items-start gap-2 px-2 py-1.5 text-[12px]">
      <span className="font-mono uppercase tracking-[0.04em] text-ink-3">{label}</span>
      <span className="whitespace-pre-wrap text-ink-3">{display}</span>
      <span className="whitespace-pre-wrap text-ink-3">{display}</span>
      <span className="text-right font-mono text-[10px] text-ink-3">=</span>
    </li>
  );
}

interface ExternalOnlySectionProps {
  title: string;
  description: string;
  rows: LmsExternalCar[];
}

function ExternalOnlySection({ title, description, rows }: ExternalOnlySectionProps) {
  const prevzit = useLmsPrevzitMutation();
  // Row key → local vehicle id once taken over; the pending row shows progress.
  const [prevzato, setPrevzato] = useState<Record<string, number>>({});
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  const onPrevzit = (key: string, body: { vin?: string; interni_cislo?: string }) => {
    setPendingKey(key);
    prevzit.mutate(body, {
      onSuccess: (res) => {
        setPrevzato((p) => ({ ...p, [key]: res.id_vozidla }));
        toast.ok('Vůz převzat z LMS');
        for (const w of res.warnings) toast.warn(w);
      },
      onError: (err) => toast.error(lmsErr(err)),
      onSettled: () => setPendingKey(null),
    });
  };

  return (
    <section className="border border-line bg-paper">
      <SectionHeader tone="neutral" title={title} count={rows.length} description={description} />
      {rows.length === 0 ? (
        <p className="px-4 py-3 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Žádné nepárované vozy.
        </p>
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((r, idx) => {
            const znackaModel = [r.Znacka, r.Model ?? r.Provedeni]
              .filter(Boolean)
              .join(' ') || '—';
            const objednavka = String(r.CisloObjednavky ?? '').trim();
            const vin = String(r.VIN ?? '').trim();
            const cislo = String(r.InterniCisloVozu ?? '').trim();
            // The server finds the car by VIN, or by internal number without one.
            const key = vin || cislo;
            const idVozidla = key ? prevzato[key] : undefined;
            return (
              <li
                key={`${r.VIN ?? ''}-${idx}`}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-[12px]"
              >
                <span className="font-mono tabular-nums text-ink">
                  {String(r.InterniCisloVozu ?? '—')}
                </span>
                <span className="font-mono tabular-nums text-ink-2">{String(r.VIN ?? '—')}</span>
                <span className="truncate text-ink">{znackaModel}</span>
                {objednavka ? (
                  <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                    Obj. {objednavka}
                  </span>
                ) : null}
                <span className="ml-auto font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                  {String(r.SEERStatus ?? '—')}
                </span>
                {idVozidla ? (
                  <Link to="/vozidla/$id" params={{ id: String(idVozidla) }}>
                    <Marker tone="ok">Převzato</Marker>
                  </Link>
                ) : key ? (
                  <Button
                    size="sm"
                    icon={<Icon name="download" size={12} />}
                    onClick={() => onPrevzit(key, vin ? { vin } : { interni_cislo: cislo })}
                    disabled={prevzit.isPending}
                  >
                    {pendingKey === key ? 'Přebírám…' : 'Převzít'}
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

interface FactoryOnlySectionProps {
  title: string;
  description: string;
  rows: { external_car: LmsExternalCar; db_car: LmsDbCar }[];
}

function FactoryOnlySection({ title, description, rows }: FactoryOnlySectionProps) {
  return (
    <section className="border border-line bg-paper">
      <SectionHeader tone="neutral" title={title} count={rows.length} description={description} />
      {rows.length === 0 ? (
        <p className="px-4 py-3 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Žádné nesoulady.
        </p>
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((r, idx) => {
            const carNum = r.db_car.vc_auta || r.external_car.InterniCisloVozu || '—';
            const model = [r.db_car.znacka_nazev, r.db_car.model_nazev]
              .filter(Boolean)
              .join(' ') || String(r.external_car.Model ?? '—');
            return (
              <li
                key={`${r.db_car.id_vozidla}-${idx}`}
                className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-2 text-[12px]"
              >
                <span className="font-mono tabular-nums text-ink-3">
                  #{r.db_car.id_vozidla}
                </span>
                {r.db_car.id_vozidla ? (
                  <Link
                    to="/vozidla/$id"
                    params={{ id: String(r.db_car.id_vozidla) }}
                    className="font-mono text-accent-text underline decoration-dotted underline-offset-2 hover:decoration-solid"
                  >
                    {carNum}
                  </Link>
                ) : (
                  <span className="font-mono text-ink">{carNum}</span>
                )}
                <span className="font-mono tabular-nums text-ink-2">
                  {String(r.external_car.VIN ?? r.db_car.vin ?? '—')}
                </span>
                <span className="truncate text-ink-2">{model}</span>
                {r.db_car.stav_vozidla ? (
                  <Marker tone={stavTone(r.db_car.stav_vozidla)}>
                    DB: {r.db_car.stav_vozidla}
                  </Marker>
                ) : null}
                <span className="ml-auto font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
                  LMS {String(r.external_car.SEERStatus ?? '—')}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

interface EqualSectionProps {
  title: string;
  count: number;
}

function EqualSection({ title, count }: EqualSectionProps) {
  return (
    <section className="border border-line bg-paper">
      <SectionHeader tone="ok" title={title} count={count} />
      <p className="px-4 py-3 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
        Vozy bez rozdílů: {formatInt(count)}.
      </p>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Local primitives
   ───────────────────────────────────────────────────────────────────────── */

interface CardProps {
  n: string;
  title: string;
  description: string;
  action: React.ReactNode;
  children: React.ReactNode;
}

function Card({ n, title, description, action, children }: CardProps) {
  return (
    <section className="border border-line bg-paper">
      <header className="flex items-start gap-4 border-b border-line px-5 py-4 max-sm:flex-wrap max-sm:px-4">
        <span className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.12em] text-accent-text">
          {n}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-[20px] leading-none tracking-[-0.02em] text-ink">
            {title}
          </h3>
          <p className="mt-1.5 text-[12px] leading-[1.5] text-ink-3">{description}</p>
        </div>
        <div className="flex-shrink-0">{action}</div>
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

function CardEmpty({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-1 py-3 text-center font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
      {children}
    </p>
  );
}

interface SectionHeaderProps {
  tone: 'accent' | 'warn' | 'ok' | 'neutral';
  title: string;
  count: number;
  description?: string;
}

function SectionHeader({ tone, title, count, description }: SectionHeaderProps) {
  return (
    <header className="flex flex-col gap-1 border-b border-line bg-bg px-4 py-2.5">
      <div className="flex items-center gap-3">
        <Marker tone={tone}>{title}</Marker>
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          {formatInt(count)} {count === 1 ? 'záznam' : 'záznamů'}
        </span>
      </div>
      {description && <p className="text-[11.5px] leading-[1.5] text-ink-3">{description}</p>}
    </header>
  );
}

const PATCHABLE_FIELDS = new Set([
  'barva',
  'motor',
  'prevodovka',
  'calouneni',
  'cenikova_cena',
  'poznamka',
  'vin',
  'provedeni',
  'umisteni',
]);

function isPatchableField(field: string): boolean {
  return PATCHABLE_FIELDS.has(field);
}

function stavTone(stav: string): 'accent' | 'ok' | 'warn' | 'neutral' {
  const s = stav.toLowerCase();
  if (s.includes('zákazn')) return 'accent';
  if (s.includes('sklad')) return 'ok';
  if (s.includes('reklam') || s.includes('vyřazen')) return 'warn';
  return 'neutral';
}

function formatFakturaInfo(db: LmsDbCar): string {
  const parts: string[] = [];
  const hf = db.hodnota_faktury;
  const hfNum = typeof hf === 'number' ? hf : hf ? Number(hf) : NaN;
  if (Number.isFinite(hfNum) && hfNum > 0) {
    parts.push(`${formatInt(hfNum)} Kč`);
  }
  const df = formatDate(db.datum_faktury ?? null);
  if (df) parts.push(df);
  return parts.length > 0 ? parts.join(' · ') : 'Bez faktury';
}

function formatChangeDate(value: string): string {
  if (!value || value === '0000-00-00' || value.startsWith('0000-')) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function lmsErr(err: unknown): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const e = (err.body as { error?: string; message?: string }).error;
    if (typeof e === 'string') return e;
    const m = (err.body as { message?: string }).message;
    if (typeof m === 'string') return m;
  }
  if (err instanceof Error) return err.message;
  return 'LMS volání selhalo.';
}
